import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Archive, BadgeCheck, Banknote, Pencil, Plus, X } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { money, nairaInputToKobo, parseMoney } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';

interface Product {
  id: string;
  name: string;
  description: string | null;
  minimumAmount: string;
  maximumAmount: string;
  minimumInstallments: number;
  maximumInstallments: number;
  repaymentFrequency: string;
  gracePeriodDays: number;
  roundingUnitNaira: number;
  minimumMembershipMonths: number;
  maxConcurrentLoans: number;
  maximumTotalExposure: string | null;
  requiredGuarantors: number;
  allowWithPriorDefault: boolean;
  eligibilityNotes: string | null;
  isActive: boolean;
  loanCount: number;
  applicationCount: number;
}

const EMPTY = {
  name: '',
  description: '',
  minimumAmount: '0',
  maximumAmount: '',
  minimumInstallments: 3,
  maximumInstallments: 12,
  repaymentFrequency: 'MONTHLY',
  gracePeriodDays: 0,
  roundingUnitNaira: 100,
  minimumMembershipMonths: 0,
  maxConcurrentLoans: 1,
  maximumTotalExposure: '',
  requiredGuarantors: 2,
  allowWithPriorDefault: false,
  eligibilityNotes: '',
  isActive: true,
};

export default function AdminLoanProducts() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Product | 'new' | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['loan-products'],
    queryFn: () => api.get<Product[]>('/loan-products'),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/loan-products/${id}`),
    onSuccess: (result) => {
      toast.success(result.message);
      void queryClient.invalidateQueries({ queryKey: ['loan-products'] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not archive.'),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Configuration"
        title="Loan products"
        description="Every product is Qard Hasan — the member repays exactly the principal."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus size={16} /> New product
          </Button>
        }
      />

      <Alert tone="info" title="There is no rate to set" className="mb-6">
        A product defines how much can be borrowed, over how long, and who qualifies. It carries no
        interest rate, processing fee or late charge — those fields do not exist anywhere in the
        system.
      </Alert>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : data.length === 0 ? (
        <EmptyState
          icon={<Banknote size={20} />}
          title="No loan products"
          description="Create a product before members can apply."
          action={<Button onClick={() => setEditing('new')}>Create a product</Button>}
        />
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th numeric>Amount range</Th>
                  <Th numeric>Repayments</Th>
                  <Th numeric>Guarantors</Th>
                  <Th numeric>Membership</Th>
                  <Th numeric>Loans</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.map((product) => (
                  <tr key={product.id} className="transition-colors hover:bg-muted/50">
                    <Td>
                      <p className="font-semibold text-foreground">{product.name}</p>
                      <p className="max-w-xs truncate text-xs text-foreground-secondary">
                        {product.description}
                      </p>
                    </Td>
                    <Td numeric>
                      {parseMoney(product.minimumAmount) > 0n
                        ? `${money(product.minimumAmount)} – ${money(product.maximumAmount)}`
                        : `Up to ${money(product.maximumAmount)}`}
                    </Td>
                    <Td numeric>
                      {product.minimumInstallments}–{product.maximumInstallments}
                    </Td>
                    <Td numeric>{product.requiredGuarantors}</Td>
                    <Td numeric>
                      {product.minimumMembershipMonths > 0
                        ? `${product.minimumMembershipMonths}m`
                        : '—'}
                    </Td>
                    <Td numeric>{product.loanCount}</Td>
                    <Td>
                      <span
                        className={
                          product.isActive
                            ? 'inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold text-success-soft-foreground ring-1 ring-inset ring-success/20'
                            : 'inline-flex items-center gap-1.5 rounded-full bg-neutral-soft px-2.5 py-1 text-xs font-semibold text-neutral-soft-foreground ring-1 ring-inset ring-neutral/20'
                        }
                      >
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(product)} aria-label="Edit">
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Archive "${product.name}"? Existing loans are unaffected; it simply stops accepting new applications.`,
                              )
                            ) {
                              archive.mutate(product.id);
                            }
                          }}
                          aria-label="Archive"
                        >
                          <Archive size={14} />
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardContent>
        </Card>
      )}

      {editing && (
        <ProductForm
          product={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['loan-products'] });
            void queryClient.invalidateQueries({ queryKey: ['public-products'] });
          }}
        />
      )}
    </div>
  );
}

function ProductForm({
  product,
  onClose,
  onDone,
}: {
  product: Product | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!product) return;
    setForm({
      name: product.name,
      description: product.description ?? '',
      minimumAmount: (Number(parseMoney(product.minimumAmount)) / 100).toString(),
      maximumAmount: (Number(parseMoney(product.maximumAmount)) / 100).toString(),
      minimumInstallments: product.minimumInstallments,
      maximumInstallments: product.maximumInstallments,
      repaymentFrequency: product.repaymentFrequency,
      gracePeriodDays: product.gracePeriodDays,
      roundingUnitNaira: product.roundingUnitNaira,
      minimumMembershipMonths: product.minimumMembershipMonths,
      maxConcurrentLoans: product.maxConcurrentLoans,
      maximumTotalExposure: product.maximumTotalExposure
        ? (Number(parseMoney(product.maximumTotalExposure)) / 100).toString()
        : '',
      requiredGuarantors: product.requiredGuarantors,
      allowWithPriorDefault: product.allowWithPriorDefault,
      eligibilityNotes: product.eligibilityNotes ?? '',
      isActive: product.isActive,
    });
  }, [product]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        description: form.description || undefined,
        eligibilityNotes: form.eligibilityNotes || undefined,
        maximumTotalExposure: form.maximumTotalExposure || null,
      };
      return product
        ? api.patch(`/loan-products/${product.id}`, payload)
        : api.post('/loan-products', payload);
    },
    onSuccess: () => {
      toast.success(product ? 'Product updated.' : 'Product created.');
      onDone();
    },
    onError: (error) => {
      if (error instanceof ApiError) setFieldErrors(error.fieldErrors);
      toast.error(error instanceof Error ? error.message : 'Could not save.');
    },
  });

  const num = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: Number(e.target.value) }));
  const text = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/50 p-0 sm:items-center sm:p-6">
      <Card className="max-h-[92vh] w-full max-w-2xl overflow-y-auto">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <CardTitle>{product ? `Edit ${product.name}` : 'New loan product'}</CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <X size={18} />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          <Field label="Name" htmlFor="name" required error={fieldErrors['name']}>
            <Input id="name" value={form.name} onChange={text('name')} />
          </Field>

          <Field label="Description" htmlFor="description" error={fieldErrors['description']}>
            <Textarea id="description" rows={2} value={form.description} onChange={text('description')} />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="Minimum amount (₦)"
              htmlFor="minAmount"
              error={fieldErrors['minimumAmount']}
              hint="0 means no floor — recommended for interest-free lending."
            >
              <Input
                id="minAmount"
                className="numeric"
                value={form.minimumAmount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, minimumAmount: e.target.value.replace(/[^\d.]/g, '') }))
                }
              />
            </Field>
            <Field label="Maximum amount (₦)" htmlFor="maxAmount" required error={fieldErrors['maximumAmount']}>
              <Input
                id="maxAmount"
                className="numeric"
                value={form.maximumAmount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, maximumAmount: e.target.value.replace(/[^\d.]/g, '') }))
                }
              />
            </Field>
          </div>

          {form.maximumAmount && (
            <div className="rounded-lg bg-success-soft p-3 text-sm text-success-soft-foreground">
              <BadgeCheck size={14} className="mr-1.5 inline" />
              A member borrowing the maximum {money(nairaInputToKobo(form.maximumAmount).toString())}{' '}
              repays exactly {money(nairaInputToKobo(form.maximumAmount).toString())}.
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Min repayments" htmlFor="minInst" required error={fieldErrors['minimumInstallments']}>
              <Input id="minInst" type="number" className="numeric" value={form.minimumInstallments} onChange={num('minimumInstallments')} />
            </Field>
            <Field label="Max repayments" htmlFor="maxInst" required error={fieldErrors['maximumInstallments']}>
              <Input id="maxInst" type="number" className="numeric" value={form.maximumInstallments} onChange={num('maximumInstallments')} />
            </Field>
            <Field label="Frequency" htmlFor="frequency" required>
              <Select id="frequency" value={form.repaymentFrequency} onChange={text('repaymentFrequency')}>
                {['WEEKLY', 'BIWEEKLY', 'MONTHLY', 'QUARTERLY'].map((option) => (
                  <option key={option} value={option}>
                    {option.charAt(0) + option.slice(1).toLowerCase()}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="Grace period (days)"
              htmlFor="grace"
              hint="Days after disbursement before the first repayment falls due."
            >
              <Input id="grace" type="number" className="numeric" value={form.gracePeriodDays} onChange={num('gracePeriodDays')} />
            </Field>
            <Field
              label="Round instalments to (₦)"
              htmlFor="rounding"
              hint="1 keeps instalments within ₦1 of each other. A coarser unit gives tidier figures."
            >
              <Input id="rounding" type="number" className="numeric" value={form.roundingUnitNaira} onChange={num('roundingUnitNaira')} />
            </Field>
          </div>

          <div className="border-t border-border pt-5">
            <p className="eyebrow mb-4">Eligibility</p>
            <p className="mb-4 text-sm text-foreground-secondary">
              With no interest to price risk into, these controls are what stand in its place.
            </p>

            <div className="grid gap-5 sm:grid-cols-3">
              <Field label="Guarantors required" htmlFor="guarantors" required error={fieldErrors['requiredGuarantors']}>
                <Input id="guarantors" type="number" min={0} max={5} className="numeric" value={form.requiredGuarantors} onChange={num('requiredGuarantors')} />
              </Field>
              <Field label="Membership (months)" htmlFor="membership">
                <Input id="membership" type="number" className="numeric" value={form.minimumMembershipMonths} onChange={num('minimumMembershipMonths')} />
              </Field>
              <Field label="Concurrent loans" htmlFor="concurrent">
                <Input id="concurrent" type="number" min={1} className="numeric" value={form.maxConcurrentLoans} onChange={num('maxConcurrentLoans')} />
              </Field>
            </div>

            <Field
              label="Maximum total exposure (₦)"
              htmlFor="exposure"
              className="mt-5"
              error={fieldErrors['maximumTotalExposure']}
              hint="Ceiling on everything one member owes across all loans. Leave blank for none."
            >
              <Input
                id="exposure"
                className="numeric"
                value={form.maximumTotalExposure}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    maximumTotalExposure: e.target.value.replace(/[^\d.]/g, ''),
                  }))
                }
              />
            </Field>

            <label className="mt-5 flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={form.allowWithPriorDefault}
                onChange={(e) => setForm((f) => ({ ...f, allowWithPriorDefault: e.target.checked }))}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
              />
              <span className="text-sm text-foreground-secondary">
                Available to members with a previous default. A prior default becomes an advisory
                note on the review page rather than a hard block.
              </span>
            </label>

            <Field label="Eligibility notes" htmlFor="eligibilityNotes" className="mt-5">
              <Textarea
                id="eligibilityNotes"
                rows={2}
                value={form.eligibilityNotes}
                onChange={text('eligibilityNotes')}
                placeholder="Anything a reviewer should know that the rules above cannot express."
              />
            </Field>

            <label className="mt-5 flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                className="h-4 w-4 accent-[hsl(var(--primary))]"
              />
              <span className="text-sm font-semibold text-foreground">
                Active — accepting new applications
              </span>
            </label>
          </div>

          <div className="flex gap-3 border-t border-border pt-5">
            <Button full loading={save.isPending} disabled={!form.name || !form.maximumAmount} onClick={() => save.mutate()}>
              {product ? 'Save changes' : 'Create product'}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
